import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListChecks, CheckCircle2, AlertTriangle, Video } from 'lucide-react';
import api from '../../utils/api';

export const meta = {
  id: 'cctv_validation_progress',
  category: 'monitoring',
  titleKey: 'dashboard.widgets.cctvProgress',
  descriptionKey: 'dashboard.widgets.cctvProgressDesc',
  defaultSize: 'xl',
  icon: ListChecks,
  // Coordination view: leadership (admin/manager) + the designated CCTV
  // coordinator(s). allowUserIds bypasses minRole for those exact users.
  minRole: 'manager',
  allowUserIds: ['cd8b13cf-8478-444b-8d8c-5b5daa87a3c8'], // Ariel Meiriño
};

// Relevamiento CCTV progress: per-company % of cameras VERIFIED in the recorder
// (the real "done" gate), plus data-incomplete flags. Coordination thermometer
// for the camera audit. Counts only — no images/credentials.
export default function CctvValidationProgressWidget({ fullscreen }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/cctv/progress');
        if (!cancelled) setData(data);
      } catch { if (!cancelled) setErr(true); }
    })();
    const iv = setInterval(async () => {
      try { const { data } = await api.get('/cctv/progress'); if (!cancelled) setData(data); } catch {}
    }, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const pct = (v, tot) => (tot > 0 ? Math.round((v / tot) * 100) : 0);
  const barColor = (p) => (p >= 100 ? '#22c55e' : p >= 60 ? '#0ea5e9' : p >= 25 ? '#f59e0b' : '#ef4444');

  return (
    <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ListChecks size={16} /> {t('dashboard.widgets.cctvProgress', 'Relevamiento CCTV')}
        </div>
        {data?.totals && (
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
            <strong style={{ color: 'var(--c-text)' }}>{pct(data.totals.verified, data.totals.total)}%</strong> {t('dashboard.widgets.cctvProgVerified', 'verificado')}
            {' · '}{data.totals.verified}/{data.totals.total}
          </span>
        )}
      </div>

      <div style={{ padding: '4px 14px 14px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {err ? (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 13, padding: 12 }}>{t('common.error', 'Error')}</div>
        ) : !data ? (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 13, padding: 12 }}>{t('common.loading', 'Cargando…')}</div>
        ) : data.companies.length === 0 ? (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 13, padding: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Video size={16} /> {t('dashboard.widgets.cctvProgEmpty', 'No hay cámaras en tu alcance.')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: fullscreen ? '1fr 1fr' : '1fr', gap: '6px 18px' }}>
            {data.companies.map(c => {
              const p = pct(c.verified, c.total);
              return (
                <div
                  key={c.id}
                  onClick={() => navigate(`/cctv?company_id=${c.id}`)}
                  style={{ padding: '7px 0', cursor: 'pointer', borderBottom: '1px solid var(--c-border)' }}
                  title={t('dashboard.widgets.cctvProgOpen', 'Abrir en Validación CCTV')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>{c.company}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--c-text-muted)' }}>
                      {c.data_incomplete > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#a855f7' }} title={t('dashboard.widgets.cctvProgIncomplete', 'cámaras con datos incompletos')}>
                          <AlertTriangle size={11} /> {c.data_incomplete}
                        </span>
                      )}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: p >= 100 ? '#16a34a' : 'var(--c-text-muted)' }}>
                        <CheckCircle2 size={11} /> {c.verified}/{c.total}
                      </span>
                      <strong style={{ color: barColor(p), minWidth: 34, textAlign: 'right' }}>{p}%</strong>
                    </span>
                  </div>
                  <div style={{ height: 7, borderRadius: 999, background: 'var(--c-surface-2)', overflow: 'hidden' }}>
                    <div style={{ width: `${p}%`, height: '100%', background: barColor(p), borderRadius: 999, transition: 'width 0.4s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
